import { useCallback } from 'react';
import {
  createFileRoute,
  Navigate,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';
import { useListOrganizations } from '@/api/trafikportalen';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import Header from '@/components/Header/Header';
import { DocumentHead } from '@/components/core/DocumentHead';
import { Loading } from '@/components/Loading';
import { TenantPeriodManagement } from '@/components/Organizations/TenantPeriodManagement';
import {
  buildPeriodPageSearch,
  collectionTypeFromPeriodSearch,
  panelFromPeriodSearch,
  parsePeriodPageSearch,
} from '@/helpers/periodPageSearch';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import type { StudentCollectionType } from '@/helpers/collectionTypes';
import type { PeriodPanel } from '@/helpers/periodPageSearch';

export const Route = createFileRoute('/$tenantId/_layout/periods/')({
  component: PeriodsPage,
  validateSearch: parsePeriodPageSearch,
  beforeLoad: ({ params }) => {
    if (!checkUserPermissions({ permission: 'tenant:viewer' })) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function PeriodsPage() {
  const { tenantId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data: organizations, isLoading } = useListOrganizations();
  const studentsDisabled = useStudentsDisabled();
  const { studentCategoryOrder } = useTenantConfiguration();
  const defaultCategory = studentCategoryOrder[0];
  const category = collectionTypeFromPeriodSearch(search, defaultCategory);
  const panel = panelFromPeriodSearch(search);

  const handleCategoryChange = useCallback(
    (next: StudentCollectionType) => {
      navigate({
        to: '/$tenantId/periods',
        params: { tenantId },
        search: buildPeriodPageSearch(next, panel, defaultCategory),
        replace: true,
      });
    },
    [defaultCategory, navigate, panel, tenantId],
  );

  const handlePanelChange = useCallback(
    (next: PeriodPanel) => {
      navigate({
        to: '/$tenantId/periods',
        params: { tenantId },
        search: buildPeriodPageSearch(category, next, defaultCategory),
        replace: true,
      });
    },
    [category, defaultCategory, navigate, tenantId],
  );

  if (studentsDisabled) {
    return <Navigate to="/$tenantId" params={{ tenantId }} replace />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="flex grow flex-col overflow-y-auto [scrollbar-gutter:stable]">
      <DocumentHead
        titleKey="page.periods.title"
        descriptionKey="page.periods.description"
      />
      <Header
        title={<FormattedMessage defaultMessage="Perioder" />}
        description={
          <FormattedMessage defaultMessage="Hantera perioder för olika elevkategorier." />
        }
      />
      <TenantPeriodManagement
        organizations={organizations ?? []}
        category={category}
        panel={panel}
        onCategoryChange={handleCategoryChange}
        onPanelChange={handlePanelChange}
      />
    </div>
  );
}
